/**
 * Registro delegado y firmas. La administración o la asistente registran un procedimiento o una evolución «en nombre de» un profesional;
 * queda «pendiente de firma» y lo firma él mismo con su PIN: desde un enlace en su celular, desde su cuenta de Donna o aquí mismo, en el equipo
 * de la clínica, sin cambiar de usuario. Nadie firma por otra persona.
 */
import { useEffect, useState } from "react";
import { api, puede } from "../api.ts";
import { AvisoError, Seccion, useAccion } from "./comun.tsx";

export interface RegistroDelegado {
  id: string; tipo: "procedimiento" | "evolucion"; texto: string; estado: "pendiente" | "observado"; motivo_observacion: string; creado: string;
  paciente_id: string; paciente: string; hc: number; profesional_id: string; profesional: string; registrado_por: string; procedimiento: string | null; diente: number | null;
}
interface ProfDelegacion { id: string; nombre: string; rol: string; telefono: string; aceptaDelegacion: boolean; tienePin: boolean; tieneEnlace: boolean; pendientes: number; observados: number }
interface MisFirmasDatos { aceptaDelegacion: boolean; tienePin: boolean; pendientes: RegistroDelegado[] }

const cuando = (iso: string) => new Date(iso).toLocaleString("es-EC", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" });
const listo = (p: ProfDelegacion) => p.aceptaDelegacion && p.tienePin;

export function Firmas() {
  return (
    <>
      <h1 className="titulo-pagina">Firmas</h1>
      {puede("clinico:delegar") && <Delegacion />}
      {puede("clinico:firmar") && <MisFirmas />}
    </>
  );
}

/* ---------------- administración y asistente ---------------- */

function Delegacion() {
  const [profs, setProfs] = useState<ProfDelegacion[]>([]);
  const [lista, setLista] = useState<RegistroDelegado[]>([]);
  const [elegidos, setElegidos] = useState<string[]>([]);
  const [pin, setPin] = useState("");
  const [motivo, setMotivo] = useState("");
  const [enlace, setEnlace] = useState<{ enlace: string; publico: boolean; profesional: string; telefono: string } | null>(null);
  const [aviso, setAviso] = useState("");
  const a = useAccion();
  const cargar = () => a.correr(async () => {
    setProfs(await api<ProfDelegacion[]>("GET", "/api/delegacion/profesionales"));
    setLista(await api<RegistroDelegado[]>("GET", "/api/delegacion/pendientes"));
  });
  useEffect(() => { void cargar(); }, []);

  const sel = lista.filter((x) => elegidos.includes(x.id));
  const profSel = sel.length && sel.every((x) => x.profesional_id === sel[0].profesional_id && x.estado === "pendiente") ? sel[0] : null;
  const alternar = (id: string) => setElegidos((e) => e.includes(id) ? e.filter((x) => x !== id) : [...e, id]);

  const hacerEnlace = (id: string) => a.correr(async () => { setEnlace(await api("POST", `/api/delegacion/profesionales/${id}/enlace`)); });
  const firmarAqui = (accion: "firmar" | "observar") => a.correr(async () => {
    if (!profSel) return;
    const r = await api<{ procesados: number }>("POST", "/api/delegacion/firmar-aqui", { profesionalId: profSel.profesional_id, ids: elegidos, accion, pin, motivo });
    setAviso(accion === "firmar" ? `Firmó ${r.procesados} registro${r.procesados === 1 ? "" : "s"} ${profSel.profesional}.` : `Quedaron observados ${r.procesados}.`);
    setPin(""); setMotivo(""); setElegidos([]);
    await cargar();
  });
  const reasignar = (id: string, profesionalId: string) => a.correr(async () => {
    await api("POST", `/api/delegacion/registros/${id}/reasignar`, { profesionalId });
    await cargar();
  });
  const wa = enlace && enlace.telefono ? `https://wa.me/593${enlace.telefono.replace(/\D/g, "").replace(/^0/, "")}?text=${encodeURIComponent(`Hola, este es tu enlace para revisar y firmar tus registros en Donna: ${enlace.enlace}`)}` : "";

  return (
    <>
      <Seccion titulo="Profesionales que firman con PIN">
        <p className="muted">Cada profesional debe autorizar el registro en su nombre y crear su PIN (una sola vez) desde su enlace. Tú puedes enviarle el enlace, pero no puedes activarlo ni firmar por él.</p>
        <AvisoError error={a.error} />
        <ul className="lista">
          {profs.map((p) => (
            <li key={p.id} className="fila" style={{ justifyContent: "space-between", flexWrap: "wrap", gap: 8 }}>
              <span><b>{p.nombre}</b> <span className={`chip ${listo(p) ? "azul" : ""}`}>{listo(p) ? "Listo para firmar" : p.aceptaDelegacion ? "Falta crear su PIN" : "Aún no autoriza"}</span>
                {p.pendientes > 0 && <span className="chip rojo"> {p.pendientes} por firmar</span>}{p.observados > 0 && <span className="chip"> {p.observados} observado{p.observados > 1 ? "s" : ""}</span>}</span>
              <span className="fila" style={{ gap: 6 }}>
                <button className="btn chico" onClick={() => hacerEnlace(p.id)} disabled={a.ocupado}>{listo(p) ? "Enlace de firma" : "Enlace para activar"}</button>
                {p.tienePin && <button className="btn chico" disabled={a.ocupado} title="Si olvidó su PIN: se borra y deberá crear otro con su contraseña de Donna"
                  onClick={() => { if (confirm(`¿Restablecer el PIN de ${p.nombre}? Deberá crear uno nuevo con su contraseña de Donna.`)) a.correr(async () => { setEnlace(await api("POST", `/api/delegacion/profesionales/${p.id}/restablecer-pin`)); await cargar(); }); }}>Restablecer PIN</button>}
              </span>
            </li>
          ))}
          {!profs.length && <li className="muted">No hay profesionales activos.</li>}
        </ul>
        {enlace && (
          <div className="aviso info">
            <b>Enlace de {enlace.profesional}</b> (el anterior dejó de funcionar):
            <input className="entrada" readOnly value={enlace.enlace} onFocus={(e) => e.currentTarget.select()} aria-label="Enlace de firma" />
            <div className="fila" style={{ gap: 8, marginTop: 6 }}>
              <button className="btn chico" onClick={() => { void navigator.clipboard?.writeText(enlace.enlace); }}>Copiar</button>
              {wa && <a className="btn chico" href={wa} target="_blank" rel="noopener">Enviar por WhatsApp</a>}
            </div>
            {!enlace.publico && <p className="muted">Este servidor no tiene dirección pública con https: el enlace solo abrirá dentro de la red de la clínica.</p>}
          </div>
        )}
      </Seccion>

      <Seccion titulo="Registros por firmar">
        {aviso && <div className="aviso info" role="status">{aviso}</div>}
        {!lista.length && <p className="muted">No hay registros pendientes ni observados.</p>}
        <ul className="lista">
          {lista.map((x) => (
            <li key={x.id} style={{ display: "block" }}>
              <label className="fila" style={{ gap: 8, alignItems: "flex-start" }}>
                {x.estado === "pendiente" && <input type="checkbox" checked={elegidos.includes(x.id)} onChange={() => alternar(x.id)} aria-label="Elegir" />}
                <span>
                  <b>{x.paciente}</b> · HC {x.hc} · <span className="muted">{cuando(x.creado)}</span> <span className={`chip ${x.estado === "observado" ? "rojo" : ""}`}>{x.estado === "observado" ? "Observado" : `Pendiente de firma del ${x.profesional}`}</span><br />
                  <span className="muted">{x.procedimiento ? `${x.procedimiento}${x.diente ? ` · pieza ${x.diente}` : ""} · ` : "Evolución · "}registró {x.registrado_por}</span><br />
                  {x.texto}
                </span>
              </label>
              {x.estado === "observado" && (
                <div className="aviso error" style={{ marginTop: 6 }}>
                  {x.profesional} indicó: «{x.motivo_observacion}».
                  <select className="entrada" defaultValue="" onChange={(e) => e.target.value && reasignar(x.id, e.target.value)} aria-label="Pasar a otro profesional">
                    <option value="">Pasar a otro profesional…</option>
                    {profs.filter((p) => p.id !== x.profesional_id && listo(p)).map((p) => <option key={p.id} value={p.id}>{p.nombre}</option>)}
                  </select>
                </div>
              )}
            </li>
          ))}
        </ul>
        {profSel && (
          <div className="aviso info" style={{ marginTop: 10 }}>
            <b>El {profSel.profesional} firma aquí, en este equipo</b>
            <p className="muted" style={{ margin: "4px 0" }}>Que lo revise en la pantalla y escriba su PIN. Tu sesión no cambia: la firma es la suya.</p>
            <input className="entrada" type="password" inputMode="numeric" maxLength={6} autoComplete="off" placeholder="PIN de 6 números" value={pin} onChange={(e) => setPin(e.target.value.replace(/\D/g, ""))} aria-label="PIN del profesional" />
            <div className="fila" style={{ gap: 8, marginTop: 6 }}>
              <button className="btn pri" disabled={a.ocupado || pin.length !== 6} onClick={() => firmarAqui("firmar")}>Firmar {sel.length} registro{sel.length > 1 ? "s" : ""}</button>
            </div>
            <input className="entrada" placeholder="Si no corresponde, escribe el motivo" maxLength={500} value={motivo} onChange={(e) => setMotivo(e.target.value)} style={{ marginTop: 8 }} />
            <button className="btn chico" disabled={a.ocupado || pin.length !== 6 || !motivo.trim()} onClick={() => firmarAqui("observar")} style={{ marginTop: 6 }}>Observar (no corresponde)</button>
          </div>
        )}
        {sel.length > 0 && !profSel && <p className="muted">Elige registros de un solo profesional para que firme aquí.</p>}
      </Seccion>
    </>
  );
}

/* ---------------- el propio profesional ---------------- */

function MisFirmas() {
  const [d, setD] = useState<MisFirmasDatos | null>(null);
  const [elegidos, setElegidos] = useState<string[]>([]);
  const [pin, setPin] = useState("");
  const [motivo, setMotivo] = useState("");
  const [clave, setClave] = useState("");
  const [pinNuevo, setPinNuevo] = useState("");
  const [aviso, setAviso] = useState("");
  const a = useAccion();
  const cargar = () => a.correr(async () => { const r = await api<MisFirmasDatos>("GET", "/api/mis-firmas"); setD(r); setElegidos((e) => e.filter((x) => r.pendientes.some((p) => p.id === x))); });
  useEffect(() => { void cargar(); }, []);
  if (!d) return <AvisoError error={a.error} />;
  const alternar = (id: string) => setElegidos((e) => e.includes(id) ? e.filter((x) => x !== id) : [...e, id]);
  const accionar = (accion: "firmar" | "observar") => a.correr(async () => {
    const r = await api<{ procesados: number }>("POST", "/api/mis-firmas/firmar", { ids: elegidos, accion, pin, motivo });
    setAviso(accion === "firmar" ? `Firmaste ${r.procesados} registro${r.procesados === 1 ? "" : "s"}.` : "Marcados para que la clínica los revise.");
    setPin(""); setMotivo(""); setElegidos([]);
    await cargar();
  });
  return (
    <>
      <Seccion titulo="Mis firmas">
        <AvisoError error={a.error} />
        {aviso && <div className="aviso info" role="status">{aviso}</div>}
        <label className="fila" style={{ gap: 8 }}>
          <input type="checkbox" checked={d.aceptaDelegacion} onChange={(e) => a.correr(async () => { await api("PUT", "/api/mis-firmas/delegacion", { acepta: e.target.checked }); await cargar(); })} />
          <span>Autorizo que la administración y la asistente registren mis procedimientos y evoluciones para que yo los revise y los firme. Nada queda firmado hasta que yo lo firme.</span>
        </label>
        <details style={{ marginTop: 8 }}>
          <summary>{d.tienePin ? "Cambiar mi PIN de firma" : "Crear mi PIN de firma"}</summary>
          <p className="muted">Para crearlo o cambiarlo escribe tu contraseña de Donna. El PIN son 6 números y es tu firma: no lo compartas.</p>
          <input className="entrada" type="password" autoComplete="current-password" placeholder="Tu contraseña de Donna" value={clave} onChange={(e) => setClave(e.target.value)} />
          <input className="entrada" type="password" inputMode="numeric" maxLength={6} autoComplete="off" placeholder="Nuevo PIN (6 números)" value={pinNuevo} onChange={(e) => setPinNuevo(e.target.value.replace(/\D/g, ""))} style={{ marginTop: 6 }} />
          <button className="btn pri" style={{ marginTop: 6 }} disabled={a.ocupado || !clave || pinNuevo.length !== 6}
            onClick={() => a.correr(async () => { await api("POST", "/api/mis-firmas/pin", { contrasena: clave, pin: pinNuevo }); setClave(""); setPinNuevo(""); setAviso("Tu PIN quedó guardado."); await cargar(); })}>Guardar PIN</button>
        </details>
      </Seccion>
      <Seccion titulo="Registrados a mi nombre, pendientes de firma">
        {!d.pendientes.length && <p className="muted">No tienes registros pendientes.</p>}
        <ul className="lista">
          {d.pendientes.map((x) => (
            <li key={x.id}>
              <label className="fila" style={{ gap: 8, alignItems: "flex-start" }}>
                <input type="checkbox" checked={elegidos.includes(x.id)} onChange={() => alternar(x.id)} aria-label="Elegir" />
                <span><b>{x.paciente}</b> · HC {x.hc} · <span className="muted">{cuando(x.creado)} · registró {x.registrado_por}</span><br />
                  <span className="muted">{x.procedimiento ? `${x.procedimiento}${x.diente ? ` · pieza ${x.diente}` : ""}` : "Evolución"}</span><br />{x.texto}</span>
              </label>
            </li>
          ))}
        </ul>
        {d.pendientes.length > 0 && (
          <>
            <input className="entrada" type="password" inputMode="numeric" maxLength={6} autoComplete="off" placeholder="Tu PIN de 6 números" value={pin} onChange={(e) => setPin(e.target.value.replace(/\D/g, ""))} aria-label="Tu PIN" />
            <div className="fila" style={{ gap: 8, marginTop: 6 }}>
              <button className="btn chico" onClick={() => setElegidos(d.pendientes.map((x) => x.id))}>Elegir todos</button>
              <button className="btn pri" disabled={a.ocupado || !elegidos.length || pin.length !== 6} onClick={() => accionar("firmar")}>Firmar los elegidos</button>
            </div>
            <input className="entrada" placeholder="Si no corresponde, escribe el motivo" maxLength={500} value={motivo} onChange={(e) => setMotivo(e.target.value)} style={{ marginTop: 8 }} />
            <button className="btn chico" style={{ marginTop: 6 }} disabled={a.ocupado || !elegidos.length || pin.length !== 6 || !motivo.trim()} onClick={() => accionar("observar")}>Observar (no corresponde)</button>
          </>
        )}
      </Seccion>
    </>
  );
}

/* ---------------- formulario «registrar en nombre de…» (Plan y Evolución) ---------------- */

export function RegistrarEnNombre({ pacienteId, item, alTerminar, alCancelar }: {
  pacienteId: string; item?: { planId: string; itemId: string; titulo: string }; alTerminar: () => void; alCancelar: () => void;
}) {
  const [profs, setProfs] = useState<ProfDelegacion[]>([]);
  const [profId, setProfId] = useState("");
  const [texto, setTexto] = useState("");
  const a = useAccion();
  useEffect(() => { api<ProfDelegacion[]>("GET", "/api/delegacion/profesionales").then(setProfs).catch(() => undefined); }, []);
  const ok = profs.find((p) => p.id === profId);
  const guardar = () => a.correr(async () => {
    await api("POST", "/api/delegacion/registros", item
      ? { pacienteId, profesionalId: profId, tipo: "procedimiento", planId: item.planId, itemId: item.itemId, texto }
      : { pacienteId, profesionalId: profId, tipo: "evolucion", texto });
    alTerminar();
  });
  return (
    <div className="aviso info" style={{ marginTop: 6 }}>
      <b>{item ? `Registrar «${item.titulo}» en nombre de un profesional` : "Registrar una evolución en nombre de un profesional"}</b>
      <p className="muted" style={{ margin: "4px 0" }}>Quedará pendiente de la firma del profesional que elijas.{item ? " El procedimiento cuenta como realizado desde ya." : ""}</p>
      <AvisoError error={a.error} />
      <select className="entrada" value={profId} onChange={(e) => setProfId(e.target.value)} aria-label="Profesional que lo realizó">
        <option value="">¿Qué profesional lo realizó?</option>
        {profs.map((p) => <option key={p.id} value={p.id} disabled={!listo(p)}>{p.nombre}{listo(p) ? "" : " (aún no autoriza ni crea su PIN)"}</option>)}
      </select>
      <textarea className="entrada" rows={3} maxLength={5000} placeholder="Evolución: qué se hizo, cómo evolucionó el paciente, indicaciones…" value={texto} onChange={(e) => setTexto(e.target.value)} style={{ marginTop: 6 }} />
      <div className="fila" style={{ gap: 8, marginTop: 6 }}>
        <button className="btn pri" disabled={a.ocupado || !ok || texto.trim().length < 10} onClick={guardar}>Registrar</button>
        <button className="btn chico" onClick={alCancelar}>Cancelar</button>
      </div>
    </div>
  );
}

/** Lista de lo pendiente de firma de un paciente (para la pestaña de evolución). */
export function PendientesDelPaciente({ pacienteId, version }: { pacienteId: string; version: number }) {
  const [lista, setLista] = useState<RegistroDelegado[]>([]);
  useEffect(() => { api<RegistroDelegado[]>("GET", `/api/pacientes/${pacienteId}/registros-delegados`).then(setLista).catch(() => setLista([])); }, [pacienteId, version]);
  if (!lista.length) return null;
  return (
    <div className="aviso info" style={{ marginBottom: 8 }}>
      <b>Pendiente de firma</b>
      <ul style={{ margin: "4px 0" }}>{lista.map((x) => (
        <li key={x.id}><span className={`chip ${x.estado === "observado" ? "rojo" : ""}`}>{x.estado === "observado" ? "Observado" : `Pendiente de firma del ${x.profesional}`}</span> {cuando(x.creado)} · {x.procedimiento ? `${x.procedimiento}${x.diente ? ` · pieza ${x.diente}` : ""}. ` : ""}{x.texto}</li>
      ))}</ul>
    </div>
  );
}
